import React, { useCallback, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { ICON_ALIASES } from '_constants/icons';
import { MODAL_KEYS } from '_constants/modals';
import { theme } from '_constants/theme';
import { usePrevious } from '_hooks';
import { selectError, selectLoading } from '_store/billing/selector';
import { createPaymentMethod } from '_store/billing/slice';
import { selectModalVisibleByKey } from '_store/modals/selector';
import { hideModal } from '_store/modals/slice';
import { selectCustomerId } from '_store/user/selector';

import Icon from 'components/atomic/Icon';
import Text from 'components/atomic/layouts/Text';
import AddPaymentMethodForm from 'components/composed/forms/AddPaymentMethodForm';

const StyledModalBody = styled(Modal.Body)`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${theme.spaces.large}px;
`;

const StyledCloseActionContainer = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  top: ${-theme.spaces.large3X}px;
  right: 0;
  font-family: 'Inter';
  font-style: normal;
  font-size: ${theme.fontSizes.regular}px;
  font-weight: 400;
  color: ${theme.colors.white};
  cursor: pointer;
`;

const StyledIconContainer = styled.div`
  margin-right: ${theme.spaces.small2X}px;
`;

const AddPaymentMethodModal = () => {
  const dispatch = useDispatch();
  const visible = useSelector(selectModalVisibleByKey(MODAL_KEYS.paymentMethodAdd));
  const loading = useSelector(selectLoading);
  const prevLoading = usePrevious(loading);
  const error = useSelector(selectError);
  const customerId = useSelector(selectCustomerId);

  const handleClose = () => {
    dispatch(hideModal({ key: MODAL_KEYS.paymentMethodAdd }));
  };

  const handleSubmit = useCallback(
    (values) => {
      if (values) {
        dispatch(createPaymentMethod({ customerId, body: { ...values } }));
      }
    },
    [dispatch, customerId]
  );

  useEffect(() => {
    if (prevLoading && !loading && !error) {
      dispatch(hideModal({ key: MODAL_KEYS.paymentMethodAdd }));
    }
  }, [dispatch, prevLoading, loading, error]);

  return (
    <Modal
      dialogClassName="payment-method-add-modal"
      show={visible}
      onHide={handleClose}
      backdrop="static"
      keyboard={false}
      centered
    >
      <StyledModalBody>
        <StyledCloseActionContainer onClick={handleClose}>
          <StyledIconContainer>
            <Icon alias={ICON_ALIASES.close} color={theme.colors.white} />
          </StyledIconContainer>
          Close
        </StyledCloseActionContainer>
        <Text
          size={theme.fontSizes.largeX}
          weight={700}
          color={theme.colors.gray.primary}
          bottomSpace={theme.spaces.medium}
        >
          Add Payment Method
        </Text>
        <AddPaymentMethodForm
          error={error}
          loading={loading}
          submitDisabled={loading}
          defaultValues={{}}
          onCancel={handleClose}
          onSubmit={handleSubmit}
        />
      </StyledModalBody>
    </Modal>
  );
};

export default AddPaymentMethodModal;
